// ============================================================================
// ExerciseDetail — the slide-over detail view for a single exercise. Opens
// with the row we already have (so it paints instantly), then refetches the
// full record via useExercise to fill instructions / secondary muscles. Shows:
//   • a demo MEDIA placeholder (the backend stores only demo_media_id today —
//     no media endpoint yet, so we render an on-brand animated placeholder)
//   • modality / level / movement-pattern / equipment meta
//   • primary + secondary muscles
//   • numbered instruction steps
// The favourite star lives in the header so it's reachable without scrolling.
// ============================================================================
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Badge } from "../../components";
import { IconStar } from "../../components/icons";
import { cn } from "../../lib/cn";
import { useExercise } from "../../hooks/exercises";
import type { Exercise } from "../../api";
import { Sheet } from "./Sheet";
import type { ExLocale } from "./lib";
import {
  exerciseDescription,
  exerciseInstructions,
  exerciseName,
  instructionSteps,
  levelTone,
  modalityOf,
  modalityTone,
  primaryMuscles,
  secondaryMuscles,
  titleCase,
} from "./lib";

interface ExerciseDetailProps {
  /** The list row we already hold (instant paint); null when closed. */
  exercise: Exercise | null;
  open: boolean;
  onClose: () => void;
  locale: ExLocale;
  favorite: boolean;
  favPending: boolean;
  onToggleFavorite: () => void;
}

export function ExerciseDetail({
  exercise,
  open,
  onClose,
  locale,
  favorite,
  favPending,
  onToggleFavorite,
}: ExerciseDetailProps) {
  const { t } = useTranslation("exercises");
  const reduce = useReducedMotion();

  // Refetch full detail (instructions live here) once we have an id.
  const detailQuery = useExercise(open ? exercise?.id : undefined);
  const ex = detailQuery.data ?? exercise;

  if (!ex) {
    return <Sheet open={open} onClose={onClose} title={t("detail.title")} wide />;
  }

  const name = exerciseName(ex, locale);
  const desc = exerciseDescription(ex, locale);
  const modality = modalityOf(ex);
  const primary = primaryMuscles(ex);
  const secondary = secondaryMuscles(ex);
  const steps = instructionSteps(exerciseInstructions(ex, locale));
  const loadingSteps = steps.length === 0 && detailQuery.isLoading;

  const meta: Array<{ label: string; value: string } | null> = [
    ex.movement_pattern
      ? {
          label: t("detail.pattern"),
          value: t(
            `values.movement_pattern.${ex.movement_pattern.toLowerCase()}`,
            titleCase(ex.movement_pattern),
          ),
        }
      : null,
    ex.equipment
      ? {
          label: t("detail.equipment"),
          value: t(
            `values.equipment.${ex.equipment.toLowerCase()}`,
            titleCase(ex.equipment),
          ),
        }
      : null,
    ex.mechanic
      ? { label: t("detail.mechanic"), value: titleCase(ex.mechanic) }
      : null,
    ex.force ? { label: t("detail.force"), value: titleCase(ex.force) } : null,
    ex.met_value != null
      ? { label: t("detail.met"), value: ex.met_value.toFixed(1) }
      : null,
  ];
  const metaRows = meta.filter((m): m is { label: string; value: string } => m !== null);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      wide
      title={name}
      subtitle={
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <Badge tone={modalityTone(modality)} solid>
            {t(`values.modality.${modality}`, titleCase(modality))}
          </Badge>
          {ex.level && (
            <Badge tone={levelTone(ex.level)}>
              {t(`values.level.${ex.level.toLowerCase()}`, titleCase(ex.level))}
            </Badge>
          )}
          {ex.is_custom === 1 && (
            <Badge tone="ember" dot>
              {t("card.custom")}
            </Badge>
          )}
        </span>
      }
      headerAccessory={
        <motion.button
          type="button"
          onClick={onToggleFavorite}
          disabled={favPending}
          whileTap={reduce ? undefined : { scale: 0.82 }}
          aria-pressed={favorite}
          aria-label={favorite ? t("card.unfavorite") : t("card.favorite")}
          className={cn(
            "grid h-9 w-9 place-items-center rounded-pill transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt focus-visible:ring-offset-2 focus-visible:ring-offset-paper",
            favorite
              ? "text-coral"
              : "text-body-faint hover:bg-surface-sunken hover:text-body-soft",
          )}
        >
          <IconStar filled={favorite} width={20} height={20} />
        </motion.button>
      }
    >
      <div className="space-y-6">
        <DemoPlaceholder name={name} />

        {desc && <p className="text-base leading-relaxed text-body-muted">{desc}</p>}

        {/* Muscles */}
        {(primary.length > 0 || secondary.length > 0) && (
          <div className="space-y-3">
            {primary.length > 0 && (
              <MuscleRow
                label={t("detail.primaryMuscles")}
                muscles={primary}
                tone="primary"
              />
            )}
            {secondary.length > 0 && (
              <MuscleRow
                label={t("detail.secondaryMuscles")}
                muscles={secondary}
                tone="secondary"
              />
            )}
          </div>
        )}

        {/* Meta grid */}
        {metaRows.length > 0 && (
          <dl className="grid grid-cols-2 gap-3 rounded-md bg-surface p-4 ring-1 ring-line sm:grid-cols-3">
            {metaRows.map((m) => (
              <div key={m.label}>
                <dt className="font-display text-xs font-bold uppercase tracking-kicker text-body-faint">
                  {m.label}
                </dt>
                <dd className="mt-0.5 font-bold text-body">{m.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {/* Instructions */}
        <section>
          <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-kicker text-body-soft">
            {t("detail.instructions")}
          </h3>
          {loadingSteps ? (
            <div className="space-y-2" aria-hidden>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-4 animate-pulse rounded-pill bg-surface-sunken"
                  style={{ width: `${90 - i * 12}%` }}
                />
              ))}
            </div>
          ) : steps.length > 0 ? (
            <ol className="space-y-3">
              {steps.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-pill bg-coral-soft font-display text-xs font-extrabold text-volt-600">
                    {i + 1}
                  </span>
                  <span className="text-sm leading-relaxed text-body-muted">
                    {step}
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="rounded-md bg-surface-sunken px-4 py-3 text-sm text-body-soft">
              {t("detail.noInstructions")}
            </p>
          )}
        </section>
      </div>
    </Sheet>
  );
}

function MuscleRow({
  label,
  muscles,
  tone,
}: {
  label: string;
  muscles: string[];
  tone: "primary" | "secondary";
}) {
  const { t } = useTranslation("exercises");
  return (
    <div>
      <p className="mb-1.5 font-display text-xs font-bold uppercase tracking-kicker text-body-faint">
        {label}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {muscles.map((m) => (
          <span
            key={m}
            className={cn(
              "rounded-pill px-3 py-1 text-sm font-bold",
              tone === "primary"
                ? "bg-coral-soft text-volt-600"
                : "bg-surface-sunken text-body-muted",
            )}
          >
            {t(`values.muscle.${m.toLowerCase()}`, titleCase(m))}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Demo-media placeholder. There's no media endpoint yet (the row carries only
 * demo_media_id), so we render an on-brand animated pulse motif rather than a
 * broken <img>. When media lands, swap this for the real asset by id.
 */
function DemoPlaceholder({ name }: { name: string }) {
  const { t } = useTranslation("exercises");
  const reduce = useReducedMotion();
  return (
    <div className="relative grid aspect-video w-full place-items-center overflow-hidden rounded-lg bg-grad-violet shadow-ember">
      <div className="absolute inset-0 bg-grad-hero-glow opacity-70" aria-hidden />
      <motion.div
        aria-hidden
        className="relative"
        animate={reduce ? undefined : { scale: [1, 1.08, 1] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      >
        <svg viewBox="0 0 96 96" className="h-20 w-20 text-white" fill="none">
          <path
            d="M16 50h14l7-22 12 40 8-26 5 8h12"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </motion.div>
      <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-pill bg-white/15 px-3 py-1 text-xs font-bold text-white backdrop-blur">
        {t("detail.demoSoon")}
      </span>
      <span className="sr-only">{name}</span>
    </div>
  );
}
